import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import SearchHeaderWidget from 'helpers/Coveo/SearchHeaderWidget';
import ButtonGroup, { ButtonGroupProps } from 'helpers/SitecoreWrappers/ButtonGroup/ButtonGroup';
import { Search } from '.generated/Feature.CW.model';
import React, { JSX, useEffect, useState } from 'react';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import classNames from 'classnames';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { CoveoEngineProvider } from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import { tv } from 'tailwind-variants';
import { SvgIcon } from 'helpers/SvgIcon';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import Sort from 'helpers/Coveo/SortDropdown';
import Pagination from 'helpers/Coveo/Pagination';
import { useCoveoEngine } from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import { buildInteractiveResult, InteractiveResult, Result } from '@coveo/headless';
import QuerySummary from 'helpers/Coveo/QuerySummary';
import { useRouter } from 'next/router';
import FacetGroup from 'helpers/Coveo/FacetGroup';
import AppliedFacetFilters from 'helpers/Coveo/AppliedFacetFilters';
import UrlManagerController from 'helpers/Coveo/UrlManagerController';
import { useAppointmentModal } from 'helpers/Context/AppointmentModalProvider';
import NoResultsMessage from 'helpers/Coveo/NoResultsMessage';
import { useSiteSettings } from 'lib/utils/page-data-static-props/pageDataContext';
import ProviderReviews from 'helpers/Reviews/ProviderReviews';
import { useCoveoSearchSubscription } from 'lib/hooks/useCoveoSearchSubscription';
import { CoveoHistoryManager } from 'helpers/Coveo/coveo-history-manager';

export type ProviderSearchProps = Search.ProviderSearch &
  ButtonGroupProps & {
    params: {
      Theme: string;
    };
    templateId: string;
  };

const ProviderSearch = (props: ProviderSearchProps): JSX.Element => {
  const fields = props?.fields;
  const router = useRouter();
  const { lat, lng } = router.query;
  const { getDictionaryValue } = useDictionary();
  const [locationCoords, setLocationCoords] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [userCoordinates, setUserCoordinates] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);

  const fieldsToInclude = [
    'cwtemplateid',
    'cwtitle',
    'cwfirstname',
    'cwlastname',
    'cwdegreetitles',
    'cwspecialties',
    'cwsearchspecialties',
    'cwacceptingnewpatients',
    'cwphoto',
    'cwschedulingtype',
    'cwschedulingtypecta',
    'cwlocation',
    'cwphonenumber',
    'clickUri',
    'uniqueId',
    'permanentid',
    'cwprimarylocationaddress',
    'cwprimarylocationaddressmapurl',
    'cwprimarylocationtitle',
    'cwgender',
    'cwprimarylocationcity',
    'cwconditions',
    'cwtreatments',
    'cwlatitude',
    'cwlongitude',
    'distanceinmiles',
    'cwitemid',
  ];
  const siteSettings = useSiteSettings()?.fields;
  const mbhPhoneNumber = siteSettings?.mbhPhoneNumber?.value; // Mental & Behavioral Health phone number
  const [coveoConfig, setCoveoConfig] = useState({
    searchHub: '',
    pipeline: '',
    accessToken: '',
    organizationId: '',
  });
  const searchFields = [
    'cwfirstname',
    'cwlastname',
    'cwdegreetitles',
    'cwspecialties',
    'cwconditions',
    'cwtreatments',
  ];
  const SearchLabelText = getDictionaryValue('SearchButtonLabel');
  const handleLocationUpdate = (coords: { latitude: number; longitude: number }) => {
    setLocationCoords(coords);
  };
  const widgetData = {
    locationSearchPlaceholderText: fields?.locationSearchPlaceholderText,
    searchButtonLabel: SearchLabelText,
    searchTermPlaceholderText: fields?.searchTermPlaceholderText,
    searchLink: fields?.searchButtonLabel,
    searchConfig: coveoConfig!,
    shouldRedirect: false,
    searchFields: searchFields,
    fieldsToInclude: fieldsToInclude,
    onLocationCoordinatesChange: handleLocationUpdate,
  };
  useEffect(() => {
    setCoveoConfig({
      searchHub: siteSettings?.providerSearchHub?.value || '',
      pipeline: siteSettings?.providerPipeline?.value || '',
      accessToken: siteSettings?.providerAccessToken?.value || '',
      organizationId: process.env.NEXT_PUBLIC_COVEO_ORG_ID || '',
    });
  }, [siteSettings]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (lat && lng) {
      const parsedLat = parseFloat(lat as string);
      const parsedLng = parseFloat(lng as string);

      if (!isNaN(parsedLat) && !isNaN(parsedLng)) {
        setLocationCoords({
          latitude: parsedLat,
          longitude: parsedLng,
        });
      }
    } else {
      const storedCoords = sessionStorage.getItem('userCoords');
      if (storedCoords) {
        const coords = JSON.parse(storedCoords);
        setUserCoordinates(coords);
      } else {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const coords = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            };
            sessionStorage.setItem('userCoords', JSON.stringify(coords));
            setUserCoordinates(coords);
          },
          (error) => {
            console.error('Geolocation error:', error);
          }
        );
      }
    }
  }, [router.isReady, lat, lng]);

  const finalCoordinates = locationCoords || userCoordinates;
  const enableDistanceSorting = !!finalCoordinates;
  const shouldSortByDistance = enableDistanceSorting;
  if (!coveoConfig?.organizationId || !coveoConfig?.accessToken || !coveoConfig?.pipeline) {
    return <></>;
  }

  return (
    <>
      <CoveoEngineProvider
        config={coveoConfig}
        userCoordinates={finalCoordinates ?? undefined}
        useDistanceSorting={shouldSortByDistance}
        fieldsToInclude={fieldsToInclude}
      >
        <ComponentWrapper className="bg-blue-50">
          <div className="max-w-screen-xxl mx-auto lg:px-[80px] px-10">
            <div className="lg:flex justify-between items-center">
              <h1 className="text-48 leading-58 font-bold text-gray-900 font-centuryGothic lg:mb-0 mb-8">
                <PlainTextWrapper field={fields?.title} />
              </h1>
              <LinkWrapper
                field={fields?.findLocationsTextButton}
                ctaVariant="tertiary"
                ctaIcon="arrow-right"
                className="text-left w-fit [&>svg]:!mb-[5px]"
              />
            </div>
            {/* Search Bar */}
            <SearchHeaderWidget {...widgetData} hasCoveoWrapper={true} />

            <div>
              <ButtonGroup
                {...props}
                WrapperClasses={classNames('lg:flex-row flex-col lg:gap-12 gap-6 flex-wrap mt-16')}
                ButtonWrapperClasses="lg:!gap-12 !gap-6"
              />
            </div>
          </div>
        </ComponentWrapper>
        <ProviderResult
          getDictionaryValue={getDictionaryValue}
          finalCoordinates={finalCoordinates ?? undefined}
          MBHPhoneNumber={mbhPhoneNumber || ''}
        />
        <UrlManagerController />
      </CoveoEngineProvider>
    </>
  );
};

export default ProviderSearch;

const ProviderResult = ({
  getDictionaryValue,
  finalCoordinates,
  MBHPhoneNumber,
}: {
  getDictionaryValue: (key: string) => string;
  finalCoordinates?: { latitude: number; longitude: number } | null;
  MBHPhoneNumber?: string | null;
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const engine = useCoveoEngine();
  const { ProviderProxyImage } = TAILWIND_VARIANTS();

  const fieldsToInclude = [
    'cwtemplateid',
    'cwtitle',
    'cwfirstname',
    'cwlastname',
    'cwdegreetitles',
    'cwspecialties',
    'cwsearchspecialties',
    'cwacceptingnewpatients',
    'cwphoto',
    'cwschedulingtype',
    'cwschedulingtypecta',
    'cwlocation',
    'cwphonenumber',
    'clickUri',
    'uniqueId',
    'permanentid',
    'cwprimarylocationaddress',
    'cwprimarylocationaddressmapurl',
    'cwprimarylocationtitle',
    'cwgender',
    'cwprimarylocationcity',
    'cwproviderlocationcount',
    'cwconditions',
    'cwtreatments',
    'cwlatitude',
    'cwlongitude',
    'distanceinmiles',
    'cwitemid',
    'cwepicid',
    'cwpgoverallrating',
    'cwpgtotalcommentcount',
    'cwpgtotalratingcount',
    'cwpressganeyurl',
  ];
  const { ContextResults, setContextResults, firstSearchExecuted } = useCoveoSearchSubscription();

  const { open } = useAppointmentModal();
  const [shouldScrollToResults, setShouldScrollToResults] = useState(false);

  // Detect if user landed with facet in URL
  useEffect(() => {
    if (
      (typeof window !== 'undefined' && window.location.hash.includes('f-')) ||
      window.location.search.includes('f-')
    ) {
      setShouldScrollToResults(true);
    }
  }, [engine, setContextResults]);

  // Scroll once results are ready
  useEffect(() => {
    if (shouldScrollToResults && engine) {
      const resultsDiv = document.querySelector('.resultlist-wrapper');
      if (resultsDiv) {
        resultsDiv.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setShouldScrollToResults(false); // prevent re-scrolling
    }
  }, [engine, shouldScrollToResults]);

  // handle location cords - latest: apply distance sort when coordinates exist or change
  useEffect(() => {
    if (!engine) return;

    const unsubscribe = engine.subscribe(() => {
      setContextResults(engine.state.search);
    });

    const locationReady = !!finalCoordinates?.latitude && !!finalCoordinates?.longitude;
    const userOverridden =
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem('coveoUserSortOverride') === '1';

    if (locationReady && !userOverridden) {
      const isAlreadyDistance = engine.state.sortCriteria === 'field:distanceinmiles ascending';
      if (!isAlreadyDistance) {
        CoveoHistoryManager.executeSilently(() => {
          engine.dispatch({
            type: 'sort/updateSortCriterion',
            payload: { by: 'field', field: 'distanceinmiles', direction: 'ascending' },
          });
        });
      }
    } else if (!locationReady && !userOverridden) {
      // No coordinates and not overridden: ensure default relevancy
      const isAlreadyRelevancy =
        engine.state.sortCriteria === 'relevancy' ||
        engine.state.sortCriteria === 'relevance' ||
        engine.state.sortCriteria === 'relevancy descending';
      if (!isAlreadyRelevancy) {
        CoveoHistoryManager.executeSilently(() => {
          engine.dispatch({
            type: 'sort/updateSortCriterion',
            payload: { by: 'relevance' },
          });
        });
      }
    }

    return () => {
      unsubscribe();
    };
  }, [engine, finalCoordinates, setContextResults]);

  // stop scroll on drawer open in mobile
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  const resultsToRender = ContextResults?.results || [];
  return (
    <>
      <ComponentWrapper className="bg-white">
        <div className="max-w-screen-xxl mx-auto lg:px-[80px] px-10 coveo-search-list">
          <div className="facet-wrapper lg:block hidden">
            <FacetGroup
              fields={[
                'cwspecialties',
                'cwacceptingnewpatients',
                'cwgender',
                'cwprimarylocationcity',
              ]}
              labels={{
                cwspecialties: 'Specialties',
                cwacceptingnewpatients: 'Accepting New Patients',
                cwgender: 'Gender',
                cwprimarylocationcity: 'City',
                //cwschedulingtype: 'Scheduling Type',
              }}
            />
          </div>
          {/* Facet Drawer for Mobile */}
          {isDrawerOpen && (
            <div
              className={classNames(
                'fixed inset-0 z-50 bg-white transform transition-transform duration-500 ease-in-out flex flex-col lg:hidden',
                {
                  'translate-x-0': isDrawerOpen,
                  'translate-x-full': !isDrawerOpen,
                }
              )}
            >
              {/* Header */}
              <div className="flex justify-between items-center px-10 py-[25px] border-b border-gray-200">
                <div className="text-20 font-bold leading-[28px] font-centuryGothic">
                  {getDictionaryValue('SetFilters')}
                </div>
                <button onClick={() => setIsDrawerOpen(false)} aria-label="Close">
                  <SvgIcon icon="close" size="lg" className="w-10 h-10 [&>path]:fill-blue-base" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-grow overflow-y-auto px-10">
                <FacetGroup
                  fields={[
                    'cwspecialties',
                    'cwacceptingnewpatients',
                    'cwgender',
                    'cwprimarylocationcity',
                  ]}
                  labels={{
                    cwspecialties: 'Specialties',
                    cwacceptingnewpatients: 'Accepting New Patients',
                    cwgender: 'Gender',
                    cwprimarylocationcity: 'City',
                    //cwschedulingtype: 'Scheduling Type',
                  }}
                />
              </div>

              {/* Footer Button */}
              <div className="p-10 border-t border-gray-200">
                <ButtonWrapper
                  ctaVariant="primary"
                  onClick={() => setIsDrawerOpen(false)}
                  className="w-full bg-blue-base text-white py-4 rounded-md text-18 font-semibold"
                >
                  {getDictionaryValue('ViewResults')}
                </ButtonWrapper>
              </div>
            </div>
          )}
          {/* Mobile only filter open button */}
          <div className="lg:hidden mb-4">
            <ButtonWrapper
              ctaVariant="secondary"
              text="Set Filters"
              ctaIcon="funnel"
              onClick={() => setIsDrawerOpen(true)}
              className="w-full [&>svg]:mt-[-5px] gap-x-4"
              ctaIconAlignment="left"
            ></ButtonWrapper>
          </div>
          <div className="py-6">
            <AppliedFacetFilters
              fields={[
                'cwspecialties',
                'cwacceptingnewpatients',
                'cwgender',
                'cwprimarylocationcity',
              ]}
              labels={{
                cwspecialties: 'Specialties',
                cwacceptingnewpatients: 'Accepting New Patients',
                cwgender: 'Gender',
                cwprimarylocationcity: 'City',
              }}
            />
          </div>
          <div className="lg:flex justify-between items-center py-6">
            <QuerySummary />
            {/* Sort control lets you select criteria to apply to search results */}
            <Sort
              fieldsToInclude={fieldsToInclude}
              enableAZSort={true} // need to add this prop if you want a-z sorting
              azSortField="cwlastname" // custom field for A–Z sort
              enableDistanceSort={!!finalCoordinates}
              //finalCoordinates={finalCoordinates}
              finalCoordinates={
                finalCoordinates
                  ? { latitude: finalCoordinates.latitude, longitude: finalCoordinates.longitude }
                  : undefined
              }
            />
          </div>
          <div className="resultlist-wrapper">
            {firstSearchExecuted ? (
              resultsToRender?.length > 0 ? (
                <div className="">
                  <div>
                    <ul className="">
                      {resultsToRender.map((item: Result, index) => {
                        const specialties = item?.raw?.cwspecialties
                          ? (item?.raw?.cwspecialties as string[])
                              .map((spec: string) => spec.trim())
                              .join(', ')
                          : '';
                        const specialtiesArray = Array.isArray(item?.raw?.cwspecialties)
                          ? (item.raw.cwspecialties as string[]).map((spec) =>
                              spec.trim().toLowerCase()
                            )
                          : [];

                        //Case-insensitive check for MBH
                        const hasMBH = specialtiesArray.includes('mental & behavioral health');

                        const queryString = `?providerName=${item?.raw?.cwfirstname} ${item?.raw?.cwlastname}&specialties=${specialties}`;
                        //const location = safeJsonParse(item?.raw?.cwlocation as string);
                        const showMoreLocationtext = getDictionaryValue('ShowXMoreLocations');
                        const degrees = item?.raw?.cwdegreetitles
                          ? (item?.raw?.cwdegreetitles as string).replaceAll(';', '/')
                          : '';
                        // if specialty is Mental & Behavioral Health set MBHPhoneNumber
                        const phoneNumber = hasMBH
                          ? MBHPhoneNumber
                          : (item?.raw?.cwphonenumber as string);

                        const schedulingType = (item?.raw?.cwschedulingtype as string)
                          ?.replaceAll(' ', '')
                          ?.toLowerCase();
                        const controller: InteractiveResult | null = engine
                          ? buildInteractiveResult(engine, {
                              options: { result: item },
                            })
                          : null;
                        // Define the standard anchor ID for the full reviews section on the profile page
                        const reviewsAnchorId = 'accordion-a20f8b32-0f19-45fd-93ec-42b39a857405';

                        return (
                          <li
                            key={index}
                            className="py-[30px] flex flex-col lg:flex-row border-b-2 border-gray-50 last:border-b-0"
                          >
                            <div className="flex-grow">
                              <div>
                                <a
                                  href={item?.clickUri}
                                  onClick={() => controller?.select()}
                                  aria-label={`${item.raw.cwfirstname as string} ${item.raw.cwlastname as string}`}
                                  onMouseDown={() => controller?.select()}
                                  className="w-fit inline-block"
                                >
                                  <h2 className="text-blue-base font-bold text-28 leading-[31px] font-centuryGothic hover:underline hover:text-darkblue-base">
                                    {item.raw.cwfirstname as string} {item.raw.cwlastname as string}
                                    {degrees && (
                                      <span>
                                        <span>{', '}</span>
                                        <span>{degrees}</span>
                                      </span>
                                    )}
                                  </h2>
                                </a>
                              </div>
                              {Array.isArray(item?.raw?.cwspecialties) && (
                                <span className="block my-5 font-normal text-18 text-gray-900">
                                  <PlainTextWrapper
                                    field={{
                                      value: item.raw.cwspecialties
                                        .map((spec: string) => spec.trim())
                                        .join(', '),
                                    }}
                                  />
                                </span>
                              )}
                              <div className="block lg:hidden w-full md:min-w-[250px] md:w-[250px] mb-8">
                                {item.raw.cwphoto ? (
                                  <ImageWrapper
                                    className="w-[100px] min-h-[125px] md:w-[270px] md:h-[270px] rounded-[8px] object-cover"
                                    field={{ value: { src: `${item.raw.cwphoto}` } }}
                                  />
                                ) : (
                                  <div className={ProviderProxyImage()}>
                                    <SvgIcon
                                      icon="user"
                                      className="!h-[60px] !w-[60px] [&>path]:fill-darkblue-base"
                                      size="lg"
                                      aria-label="Provider Profile Video"
                                    />
                                  </div>
                                )}
                              </div>
                              {/* Ratings & Reviews*/}
                              <ProviderReviews
                                variant="search-card"
                                profilePageUrlOrAnchorId={reviewsAnchorId}
                                coveoSearchData={item}
                              />
                              {(item.raw.cwacceptingnewpatients as string) && (
                                <div
                                  className={`${(item.raw.cwacceptingnewpatients as string).toLowerCase() === 'not accepting new patients' ? 'bg-utility-red-50' : 'bg-utility-green-50'} text-gray-900 font-nunitoSans text-18 font-normal leading-[31px] md:inline-flex flex md:items-center items-start lg:mt-16 mt-4 px-8 py-3 rounded-[6px] mb-12`}
                                >
                                  <SvgIcon
                                    icon={`${(item.raw.cwacceptingnewpatients as string).toLowerCase() === 'not accepting new patients' ? 'xcircle' : 'check-circle'}`}
                                    className={`${(item.raw.cwacceptingnewpatients as string).toLowerCase() === 'not accepting new patients' ? '[&>path]:fill-utility-red-700' : '[&>path]:fill-utility-green-700'} mr-4 !h-[22px] !w-[22px]`}
                                    size="lg"
                                    aria-label={item.raw.cwacceptingnewpatients}
                                  />
                                  <span>{item.raw.cwacceptingnewpatients as string}</span>
                                </div>
                              )}
                              <div className="flex gap-4">
                                {(item.raw.cwprimarylocationtitle as string) && (
                                  <span className="inline-block text-orange-700">
                                    <SvgIcon icon={'map-pin'} size="lg" />
                                  </span>
                                )}
                                <div className="text-18">
                                  {(item?.raw?.cwprimarylocationtitle as string) && (
                                    <div className="relative mb-5">
                                      <PlainTextWrapper
                                        field={{
                                          value: item?.raw?.cwprimarylocationtitle as string,
                                        }}
                                      />
                                    </div>
                                  )}
                                  {(item.raw.cwprimarylocationaddressmapurl as string) && (
                                    <div className="relative mb-5">
                                      {item.raw.cwprimarylocationaddressmapurl ? (
                                        <LinkWrapper
                                          className="hover:underline text-gray-900 block group "
                                          field={{
                                            value: {
                                              href: item.raw.cwprimarylocationaddressmapurl,
                                              text: ' ', // dummy text to avoid rendering default icon
                                              target: '_blank',
                                            },
                                          }}
                                        >
                                          <div className="group-hover:underline">
                                            <PlainTextWrapper
                                              field={{
                                                value: item?.raw
                                                  ?.cwprimarylocationaddress as string,
                                              }}
                                            />
                                          </div>
                                          <div className="mt-1 inline-flex items-center gap-1 group-hover:underline">
                                            {`${getDictionaryValue('GetDirections')}`}
                                          </div>
                                        </LinkWrapper>
                                      ) : (
                                        <PlainTextWrapper
                                          field={{
                                            value: item?.raw?.cwprimarylocationaddress as string,
                                          }}
                                        />
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {(item?.raw?.cwproviderlocationcount as number) > 1 &&
                                (() => {
                                  const remainingCount =
                                    (item?.raw?.cwproviderlocationcount as number) - 1;
                                  let linkText = showMoreLocationtext.replace(
                                    '{X}',
                                    String(remainingCount)
                                  );

                                  if (remainingCount === 1) {
                                    // Remove plural 's'
                                    linkText = linkText.replace('locations', 'location');
                                  }

                                  return (
                                    <LinkWrapper
                                      ctaVariant="hyperlink"
                                      field={{
                                        value: {
                                          href: `${item?.clickUri}?showMoreLocation=true`,
                                          text: linkText,
                                        },
                                      }}
                                      className="!justify-start mt-6 text-left lg:ml-[32px] ml-12 lg:mb-0 mb-12 inline-block w-fit"
                                    />
                                  );
                                })()}
                            </div>
                            <div className="flex gap-x-24">
                              <div className="lg:max-w-[300px] w-full">
                                {(() => {
                                  const isPrimaryCare =
                                    schedulingType?.toLocaleLowerCase() === 'primarycare' &&
                                    !!item?.raw?.cwepicid;
                                  const isSpecialtyCare =
                                    schedulingType?.toLocaleLowerCase() !== 'primarycare' &&
                                    !!item?.raw?.cwschedulingtypecta;

                                  // Precedence: SpecialtyCare > PrimaryCare
                                  const showRequest = isSpecialtyCare;
                                  const showSchedule = !showRequest && isPrimaryCare; // only if not specialty

                                  const hasAppointmentOption =
                                    showRequest || showSchedule || !!phoneNumber;
                                  const schedulingButtonDictionaryKey =
                                    schedulingType === 'urgentcare'
                                      ? 'ReserveATime'
                                      : 'RequestAnAppointment';

                                  return (
                                    <>
                                      {hasAppointmentOption ? (
                                        <span className="block font-bold text-18 leading-31">
                                          <PlainTextWrapper
                                            field={{
                                              value: `${getDictionaryValue('ToMakeAnAppointment')}:`,
                                            }}
                                          />
                                        </span>
                                      ) : (
                                        // added this !isPrimaryCare because CW team decided to hide AppointmentNotAvailable text if there is no EPIC ID.
                                        <div
                                          className={`${!isPrimaryCare ? 'hidden' : 'block'} text-18 text-gray-900 leading-[28px] font-normal mt-6`}
                                        >
                                          <PlainTextWrapper
                                            field={{
                                              value: `${getDictionaryValue('AppointmentNotAvailable')}`,
                                            }}
                                          />
                                        </div>
                                      )}

                                      <div className="flex lg:flex-col md:flex-row flex-col lg:gap-0 md:gap-12">
                                        {showSchedule && (
                                          <ButtonWrapper
                                            text={`${getDictionaryValue('ScheduleAnAppointment')}`}
                                            ctaVariant="primary"
                                            className="w-full min-w-[300px] md:w-1/2 mt-6"
                                            onClick={() =>
                                              open(
                                                item?.raw?.cwitemid
                                                  ? (item?.raw?.cwitemid as string)
                                                  : '',
                                                queryString,
                                                phoneNumber || ''
                                              )
                                            }
                                          />
                                        )}

                                        {showRequest && (
                                          <LinkWrapper
                                            field={{
                                              value: {
                                                href: `${item?.raw?.cwschedulingtypecta}${queryString}`,
                                                text: `${getDictionaryValue(schedulingButtonDictionaryKey)}`,
                                                title: `${getDictionaryValue(schedulingButtonDictionaryKey)}`,
                                              },
                                            }}
                                            suppressNewTabIcon={true}
                                            ctaVariant="primary"
                                            className="w-full min-w-[300px] md:w-1/2 mt-6"
                                          />
                                        )}

                                        {phoneNumber && (
                                          <LinkWrapper
                                            field={{
                                              value: {
                                                href: `tel:${phoneNumber}`,
                                                text: `${getDictionaryValue('Call')} ${phoneNumber}`,
                                              },
                                            }}
                                            ctaVariant="secondary"
                                            className="w-full min-w-[300px] md:w-1/2 mt-6 !leading-22"
                                          />
                                        )}
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                              <div className="hidden lg:block max-w-[160px] w-full">
                                {item.raw.cwphoto ? (
                                  <ImageWrapper
                                    className="lg:min-w-[160px] w-full min-h-[200px] rounded-[8px] object-cover"
                                    field={{ value: { src: `${item.raw.cwphoto}` } }}
                                  />
                                ) : (
                                  <div className={ProviderProxyImage()}>
                                    <SvgIcon
                                      icon="user"
                                      className="!h-[80px] !w-[80px] [&>path]:fill-darkblue-base"
                                      size="lg"
                                      aria-label="Provider Profile Video"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              ) : (
                <>
                  <NoResultsMessage />
                </>
              )
            ) : null}
          </div>

          <div className="pagination-wrapper">
            <Pagination fieldsToInclude={fieldsToInclude} className="justify-end mt-12" />
          </div>
        </div>
      </ComponentWrapper>
    </>
  );
};

const TAILWIND_VARIANTS = tv({
  defaultVariants: {
    style: 'primary',
  },
  slots: {
    ProviderProxyImage: [
      'bg-blue-100',
      'rounded-md',
      'mb-[26px]',
      'lg:min-w-[160px]',
      'lg:w-[160px] lg:h-[200px]',
      'md:w-[160px] md:h-[200px]',
      'w-[100px] h-[125px]',
      'flex items-center justify-center',
    ],
  },

  variants: {},
});
